import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipLoader } from "react-spinners";
import { useLocation, useNavigate } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';

function UpdateTour() {
  const location = useLocation();
  const navigate = useNavigate();

  const [tourId, setTourId] = useState(null);
  const [tourTitle, setTourTitle] = useState('');
  const [tourDescription, setTourDescription] = useState('');
  const [tourRate, setTourRate] = useState('');
  const [dayCount, setDayCount] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [showInputs, setShowInputs] = useState(false);

  // NEW: Route overview state
  const [routeDescription, setRouteDescription] = useState('');
  const [routeImageFile, setRouteImageFile] = useState(null);
  const [routeImagePreview, setRouteImagePreview] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (location.state && location.state.TourId) {
      setTourId(location.state.TourId);
      fetchTourDetails(location.state.TourId);
    }
  }, [location]);

  const fetchTourDetails = async (tourId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `https://genzserver.mylksystems.com/Tours/getTourbyId`,
        { tourID: tourId }
      );
      const tourData = response.data.data;
      if (!tourData || tourData.length === 0) return;

      const firstItem = tourData[0];

      console.log(firstItem);

      // Title / cover / rate
      setTourTitle(firstItem.Title || '');
      // Adjust if your API returns a different field for cover description
      setTourDescription(firstItem.CoverDescription ?? firstItem.Description ?? '');
      setTourRate(firstItem.rate ?? '');

      const routePhotoName = firstItem.RoutePhoto;
      setRouteImagePreview(
        routePhotoName ? `https://genzserver.mylksystems.com/Assets/Tours/${routePhotoName}` : ''
      );
      setRouteDescription(firstItem.RouteDescription || '');

      // Days
      setDayCount(tourData.length);
      const dayInputs = tourData.map((element) => ({
        description: element.dayDescription || element.Description || '',
        photo: element.Photo
          ? `https://genzserver.mylksystems.com/Assets/Tours/${element.Photo}`
          : null,
        isFromServer: true,
      }));
      setInputs(dayInputs);
      setShowInputs(true);
    } catch (error) {
      console.error('Error fetching tour details:', error);
    } finally {
      setLoading(false);
      setFetching(false);
    }
  };

  const handlePhotoChange = (index, file) => {
    const updated = [...inputs];
    updated[index].photo = file;
    updated[index].isFromServer = false;
    setInputs(updated);
  };

  const handleDescriptionChange = (index, desc) => {
    const updated = [...inputs];
    updated[index].description = desc;
    setInputs(updated);
  };

  // NEW: route image chooser + live preview
  const handleRouteImageChange = (file) => {
    setRouteImageFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setRouteImagePreview(url);
    } else {
      setRouteImagePreview('');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append('tourID', tourId);
    formData.append('title', tourTitle);
    formData.append('description', tourDescription);
    formData.append('rate', tourRate);
    formData.append('days', dayCount);

    // NEW: route fields (backend expects these exact keys)
    formData.append('route_description', routeDescription || '');
    if (routeImageFile) {
      formData.append('route_image', routeImageFile);
    }

    // Non-file day inputs (keeps existing photos when isFromServer=true)
    const nonFileInputs = inputs.map((input) => ({
      description: input.description,
      isFromServer: input.isFromServer,
      photo: input.photo ? input.photo : null,
    }));
    formData.append('inputs', JSON.stringify(nonFileInputs));

    // Append files for days where a new file was chosen
    inputs.forEach((input, index) => {
      if (input.photo && input.photo instanceof File) {
        formData.append(`day_${index + 1}_photo`, input.photo);
      }
    });

    try {
      await axios.post('https://genzserver.mylksystems.com/Tours/updateTour', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Tour updated successfully!');
      navigate('/ManageTours');
    } catch (error) {
      console.error('Error updating tour:', error);
      alert('Failed to update the tour. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell page-shell-narrow">
      <header className="page-header">
        <div>
          <span className="eyebrow">Tour maintenance</span>
          <h1 className="page-title">Update tour</h1>
          <p className="page-copy">
            Refresh the tour story, route photo, and day-by-day itinerary without changing API contracts.
          </p>
        </div>
      </header>

      <div className="admin-card form-card">
        {fetching ? (
          <div className="empty-state">
            <ClipLoader color="var(--accent)" size={50} />
          </div>
        ) : (
          <form onSubmit={handleFormSubmit} className="form-grid">
            <div className="form-note">
              The number of days stays fixed here. Update the existing route and day details below.
            </div>

            <div className="field">
              <label>Tour Title</label>
              <textarea
                value={tourTitle}
                onChange={(e) => setTourTitle(e.target.value)}
                rows={3}
                placeholder="Enter a short title for the tour"
                required
              />
            </div>

            <div className="field">
              <label>Tour Description</label>
              <textarea
                value={tourDescription}
                onChange={(e) => setTourDescription(e.target.value)}
                rows={3}
                placeholder="Enter a short description for the tour"
                required
              />
            </div>

            <div className="field">
              <label>Tour Rate</label>
              <input
                value={tourRate}
                onChange={(e) => setTourRate(e.target.value)}
                placeholder="Enter Tour Rate in LKR"
                type="number"
                min="0"
                required
              />
            </div>

            <section className="section-card">
              <h2 className="section-title">Route Overview</h2>

              {routeImagePreview && (
                <img src={routeImagePreview} alt="Route preview" className="image-preview mb-3" />
              )}

              <div className="form-grid">
                <div className="field">
                  <label>Route Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleRouteImageChange(e.target.files?.[0] || null)}
                  />
                </div>

                <div className="field">
                  <label>Route Description</label>
                  <textarea
                    value={routeDescription}
                    onChange={(e) => setRouteDescription(e.target.value)}
                    rows={4}
                    placeholder="Describe the overall route: start/end, main attractions, highlights"
                  />
                </div>
              </div>
            </section>

            {showInputs &&
              inputs.map((input, index) => (
                <section className="section-card" key={index}>
                  <h2 className="section-title">Day {index + 1}</h2>

                  {input.photo && typeof input.photo === 'string' && (
                    <img src={input.photo} alt={`Day ${index + 1}`} className="image-preview mb-3" />
                  )}

                  <div className="form-grid">
                    <div className="field">
                      <label>Photo</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoChange(index, e.target.files[0])}
                      />
                    </div>

                    <div className="field">
                      <label>Description</label>
                      <textarea
                        value={input.description}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        rows={4}
                        placeholder={`Enter description for Day ${index + 1}`}
                        required
                      />
                    </div>
                  </div>
                </section>
              ))}

            {showInputs && (
              <button type="submit" className="admin-button" disabled={loading}>
                {loading ? <ClipLoader color="#ffffff" size={20} /> : <SaveIcon fontSize="small" />}
                {loading ? 'Updating...' : 'Update Tour'}
              </button>
            )}

            {!showInputs && (
              <div className="form-note">
                <ImageIcon fontSize="small" /> No itinerary days were returned for this tour.
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

export default UpdateTour;
